/**
 * @module KatapultPhotoUploadUpdateComposers
 * Update composers for the data written when a photo is uploaded and when it is counted.
 */

import { increment, serverTimestamp } from 'firebase/database';
import { composeItemPhotoAssociationUpdate, composeItemPhotoDataUpdate } from '../KatapultJobUpdateComposers.js';
import { Path } from '../../Path.js';
import { GetPhotoSummary } from '../../GetPhotoSummary.js';

/**
 * @typedef {object} PhotoAssociation
 * @property {string} [nodeId]
 * @property {string} [connectionId]
 * @property {string} [sectionId]
 */

/**
 * @typedef {object} PhotoFolder
 * @property {number} [date_created]
 * @property {string} [job_id]
 * @property {string} [status]
 * @property {Record<string, {date_created?: number}>} [cameras]
 */

/**
 * @typedef {object} UploadedPhoto
 * @property {string} photoId
 * @property {string} folderId The photo folder the photo is uploaded into.
 * @property {string} cameraId The camera within the photo folder.
 * @property {Record<string, any>|null} [exif] The EXIF read from the photo file.
 * @property {string} uploadedBy
 * @property {PhotoAssociation} [association]
 * @property {Record<string, any>|null} [associatedPhotoUpdate] Data for the photo record under each associated item.
 * @property {Record<string, any>|null} [photoUpdate] Data for the photo record itself.
 */

/**
 * Compose the job write for one uploaded photo's record, summary, and item associations, without an upload status.
 * @param {UploadedPhoto} uploadedPhoto
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
export function composeUploadedPhotoDataUpdate(uploadedPhoto) {
  // Build the photo record from the EXIF, the upload details, and the photo's own data
  const { photoId, association, associatedPhotoUpdate } = uploadedPhoto;
  const photoRecord = buildUploadedPhotoRecord(uploadedPhoto);

  // Combine the record, upload date, summary, and associated item updates
  const photoRecordUpdate = flattenIntoFirebaseUpdate({ [`photos/${photoId}`]: photoRecord });
  const uploadDateUpdate = { [`photos/${photoId}/upload_date`]: serverTimestamp() };
  const photoSummaryUpdate = composeUploadedPhotoSummaryUpdate(photoId, photoRecord);
  const associatedItemsUpdate = composeUploadedPhotoAssociatedItemsUpdate(photoId, association, associatedPhotoUpdate);
  return { ...photoRecordUpdate, ...uploadDateUpdate, ...photoSummaryUpdate, ...associatedItemsUpdate };
}

/**
 * Compose the job write that records an uploaded photo on its folder and camera and counts it there.
 * @param {string} jobId
 * @param {Record<string, any>} photoRecord The photo record as stored on the server, holding its folder and camera ids.
 * @param {PhotoFolder|null} photoFolder The photo's folder as stored, or null when it does not exist yet.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
export function composeUploadedPhotoFolderUpdate(jobId, photoRecord, photoFolder) {
  // Find the folder and camera the photo was uploaded into
  const cameraId = photoRecord.camera_id;
  const folderPath = `photo_folders/${photoRecord.folder_id}`;
  const cameraPath = `${folderPath}/cameras/${cameraId}`;

  // Fill in the folder and camera identity fields that are missing
  /** @type {Record<string, any>} */
  const photoFolderUpdate = {};
  if (photoFolder?.date_created == null) photoFolderUpdate[`${folderPath}/date_created`] = serverTimestamp();
  if (photoFolder?.job_id == null) photoFolderUpdate[`${folderPath}/job_id`] = jobId;
  if (photoFolder?.status == null) photoFolderUpdate[`${folderPath}/status`] = 'Uploading';
  const cameraDateCreated = photoFolder?.cameras?.[cameraId]?.date_created;
  if (cameraDateCreated == null) photoFolderUpdate[`${cameraPath}/date_created`] = serverTimestamp();

  // Mark the folder done and record when the folder and camera last received a photo
  photoFolderUpdate[`${folderPath}/done`] = true;
  photoFolderUpdate[`${folderPath}/last_upload`] = serverTimestamp();
  photoFolderUpdate[`${cameraPath}/last_upload`] = serverTimestamp();

  // Increment each counter the photo is counted in on the folder and camera
  const counterNames = listUploadedPhotoCounterNames(photoRecord);
  for (const counterName of counterNames) {
    photoFolderUpdate[`${folderPath}/${counterName}`] = increment(1);
    photoFolderUpdate[`${cameraPath}/${counterName}`] = increment(1);
  }
  return photoFolderUpdate;
}

/**
 * Compose the write that counts an uploaded photo on every sharing company's job counters.
 * @param {string} jobId
 * @param {Record<string, any>} photoRecord The photo record as stored on the server.
 * @param {Record<string, string>|null} sharing The job's sharing object.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to photoheight.
 */
export function composeUploadedPhotoSharingCompanyCounterUpdate(jobId, photoRecord, sharing) {
  // List the counters the photo is counted in and the companies it is counted for
  const counterNames = listUploadedPhotoCounterNames(photoRecord);
  const sharingCompanyIds = Object.keys(sharing ?? {});

  // Increment each counter and record when it changed for every sharing company
  /** @type {Record<string, any>} */
  const companyCounterUpdate = {};
  for (const companyId of sharingCompanyIds) {
    for (const counterName of counterNames) {
      companyCounterUpdate[`counters/${companyId}/${jobId}/${counterName}/value`] = increment(1);
      companyCounterUpdate[`counters/${companyId}/${jobId}/${counterName}/last_modified`] = serverTimestamp();
    }
  }
  return companyCounterUpdate;
}

/**
 * Compose the job write that records a photo the old upload flow touched on its folder and camera.
 * @param {string} jobId
 * @param {Record<string, any>} photoRecord The photo record as stored on the server, holding its folder and camera ids.
 * @param {PhotoFolder|null} photoFolder The photo's folder as stored, or null when it does not exist yet.
 * @param {string} legacyUploadStatus The status the old upload flow left on the photo.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
export function composeLegacyUploadedPhotoFolderUpdate(jobId, photoRecord, photoFolder, legacyUploadStatus) {
  // Find the folder and camera the photo was uploaded into
  const cameraId = photoRecord.camera_id;
  const folderPath = `photo_folders/${photoRecord.folder_id}`;
  const cameraPath = `${folderPath}/cameras/${cameraId}`;

  // Fill in the folder and camera identity fields that are missing
  /** @type {Record<string, any>} */
  const photoFolderUpdate = {};
  if (photoFolder?.date_created == null) photoFolderUpdate[`${folderPath}/date_created`] = serverTimestamp();
  if (photoFolder?.job_id == null) photoFolderUpdate[`${folderPath}/job_id`] = jobId;
  if (photoFolder?.status == null) photoFolderUpdate[`${folderPath}/status`] = 'Uploading';
  const cameraDateCreated = photoFolder?.cameras?.[cameraId]?.date_created;
  if (cameraDateCreated == null) photoFolderUpdate[`${cameraPath}/date_created`] = serverTimestamp();

  // Mark the folder done and record when the folder and camera last received a photo
  photoFolderUpdate[`${folderPath}/done`] = true;
  photoFolderUpdate[`${folderPath}/last_upload`] = serverTimestamp();
  photoFolderUpdate[`${cameraPath}/last_upload`] = serverTimestamp();

  // Increment each counter the old upload flow left uncounted on the folder and camera
  const counterNames = listLegacyUploadedPhotoUncountedCounterNames(photoRecord, legacyUploadStatus);
  for (const counterName of counterNames) {
    photoFolderUpdate[`${folderPath}/${counterName}`] = increment(1);
    photoFolderUpdate[`${cameraPath}/${counterName}`] = increment(1);
  }
  return photoFolderUpdate;
}

/**
 * Compose the write that counts a photo the old upload flow touched on every sharing company's job counters.
 * @param {string} jobId
 * @param {Record<string, any>} photoRecord The photo record as stored on the server.
 * @param {Record<string, string>|null} sharing The job's sharing object.
 * @param {string} legacyUploadStatus The status the old upload flow left on the photo.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to photoheight.
 */
export function composeLegacyUploadedPhotoSharingCompanyCounterUpdate(jobId, photoRecord, sharing, legacyUploadStatus) {
  // List the counters the old upload flow left uncounted and the companies they are counted for
  const counterNames = listLegacyUploadedPhotoUncountedCounterNames(photoRecord, legacyUploadStatus);
  const sharingCompanyIds = Object.keys(sharing ?? {});

  // Increment each counter and record when it changed for every sharing company
  /** @type {Record<string, any>} */
  const companyCounterUpdate = {};
  for (const companyId of sharingCompanyIds) {
    for (const counterName of counterNames) {
      companyCounterUpdate[`counters/${companyId}/${jobId}/${counterName}/value`] = increment(1);
      companyCounterUpdate[`counters/${companyId}/${jobId}/${counterName}/last_modified`] = serverTimestamp();
    }
  }
  return companyCounterUpdate;
}

/**
 * Compose the job write that marks a photo upload finished and complete.
 * @param {string} photoId
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
export function composePhotoUploadFinishUpdate(photoId) {
  // Mark the upload finished and complete and clear the photo summary status
  return {
    [`photos/${photoId}/upload_state/photo_upload_finished`]: true,
    [`photos/${photoId}/status`]: 'upload_complete',
    [`photo_summary/${photoId}/status`]: null
  };
}

/**
 * Build the photo record of an uploaded photo without a status or upload state.
 * @param {UploadedPhoto} uploadedPhoto
 * @returns {Record<string, any>} The photo record without a rotation, status, or upload state.
 */
function buildUploadedPhotoRecord(uploadedPhoto) {
  // Combine the EXIF with the upload details, leaving out the rotation
  const { folderId, cameraId, uploadedBy, exif, photoUpdate } = uploadedPhoto;
  const uploadFields = { camera_id: cameraId, folder_id: folderId, uploaded_by: uploadedBy };
  /** @type {Record<string, any>} */
  const photoRecord = { ...exif, ...uploadFields };
  delete photoRecord.rotation;

  // Write the photo's own data into the record, leaving out any status or upload state
  const photoUpdateEntries = Object.entries(photoUpdate ?? {});
  photoUpdateEntries.forEach(([fieldPath, value]) => Path.set(photoRecord, fieldPath, value, '/'));
  delete photoRecord.status;
  delete photoRecord.upload_state;
  return photoRecord;
}

/**
 * Compose the photo summary write of an uploaded photo.
 * @param {string} photoId
 * @param {Record<string, any>} photoRecord The record built by buildUploadedPhotoRecord.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
function composeUploadedPhotoSummaryUpdate(photoId, photoRecord) {
  // Summarize the photo record without a priority
  const photoSummary = GetPhotoSummary(photoRecord);
  delete photoSummary['.priority'];

  // Return the summary flattened for Firebase
  const photoSummaryUpdate = { [`photo_summary/${photoId}`]: photoSummary };
  return flattenIntoFirebaseUpdate(photoSummaryUpdate);
}

/**
 * Compose the write that associates an uploaded photo to its node and section and writes its data under each.
 * @param {string} photoId
 * @param {PhotoAssociation} [association]
 * @param {Record<string, any>|null} [associatedPhotoUpdate] Data for the photo record under each associated item.
 * @returns {Record<string, any>} Leaf values keyed by paths relative to the job.
 */
function composeUploadedPhotoAssociatedItemsUpdate(photoId, association, associatedPhotoUpdate) {
  // List the node and section the photo associates to
  const { nodeId, connectionId, sectionId } = association ?? {};
  const associatedItems = [];
  if (nodeId != null) associatedItems.push({ itemType: 'node', itemId: nodeId });
  if (connectionId != null && sectionId != null) associatedItems.push({ itemType: 'section', itemId: `${connectionId}:${sectionId}` });

  // Associate the photo to each item and write the item's photo data
  const associatedItemsUpdate = {};
  for (const { itemType, itemId } of associatedItems) {
    const itemPhotoAssociationUpdate = composeItemPhotoAssociationUpdate(itemType, itemId, photoId, true);
    const itemPhotoDataUpdate = associatedPhotoUpdate ? composeItemPhotoDataUpdate(itemType, itemId, photoId, associatedPhotoUpdate) : {};
    Object.assign(associatedItemsUpdate, itemPhotoAssociationUpdate, itemPhotoDataUpdate);
  }

  // Return the update flattened for Firebase
  return flattenIntoFirebaseUpdate(associatedItemsUpdate);
}

/**
 * List the counters an uploaded photo is counted in.
 * @param {Record<string, any>} photoRecord The photo record as stored on the server.
 * @returns {string[]} The total and uploaded counter names, plus tagged and associated when they apply.
 */
function listUploadedPhotoCounterNames(photoRecord) {
  // Read whether the photo holds photo data or an association from its summary
  const photoSummary = GetPhotoSummary(photoRecord);

  // Count the photo in the total and uploaded counters, plus tagged and associated when they apply
  const counterNames = ['numPhotos', 'numUploaded'];
  if (photoSummary.data === true) counterNames.push('numTagged');
  if (photoSummary.associated === true) counterNames.push('numAssociated');
  return counterNames;
}

/**
 * List the counters the old upload flow left uncounted for a photo.
 * @param {Record<string, any>} photoRecord The photo record as stored on the server.
 * @param {string} legacyUploadStatus The status the old upload flow left on the photo.
 * @returns {string[]} The uploaded counter name unless the old flow completed, plus tagged and associated when they apply.
 */
function listLegacyUploadedPhotoUncountedCounterNames(photoRecord, legacyUploadStatus) {
  // Read whether the photo holds photo data or an association from its summary
  const photoSummary = GetPhotoSummary(photoRecord);

  // Count the photo as uploaded unless the old flow completed, plus tagged and associated when they apply
  const counterNames = [];
  if (legacyUploadStatus !== 'upload_complete') counterNames.push('numUploaded');
  if (photoSummary.data === true) counterNames.push('numTagged');
  if (photoSummary.associated === true) counterNames.push('numAssociated');
  return counterNames;
}

// Flatten nested objects into slash paths and drop undefined values
function flattenIntoFirebaseUpdate(update) {
  const flattenedUpdate = Path.flattenObject(update, '/');
  const storableEntries = Object.entries(flattenedUpdate).filter(([, value]) => value !== undefined);
  return Object.fromEntries(storableEntries);
}
